import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  BarChart3, 
  TrendingUp, 
  Users, 
  FileText,
  ExternalLink,
  Eye,
  EyeOff
} from 'lucide-react';
import ApiService from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';

interface ReportesConfigProps {
  onBack: () => void;
}

interface Reporte {
  id: number;
  nombre: string;
  descripcion: string;
  webUrl: string;
  embedUrl: string;
  datasetId: string;
  icono: string;
  categoria: string;
  activo: boolean;
  fechaCreacion: string;
  rolesPermitidos?: string[];
  orden: number;
}

interface ReporteFormData {
  nombre: string;
  descripcion: string;
  webUrl: string;
  embedUrl: string;
  datasetId: string;
  icono: string;
  categoria: string;
  activo: boolean;
  rolesPermitidos: string[];
  orden: number;
}

const ReportesConfig: React.FC<ReportesConfigProps> = ({ onBack }) => {
  const [reportes, setReportes] = useState<Reporte[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReporte, setEditingReporte] = useState<Reporte | null>(null);
  const [formData, setFormData] = useState<ReporteFormData>({
    nombre: '',
    descripcion: '',
    webUrl: '',
    embedUrl: '',
    datasetId: '',
    icono: 'BarChart3',
    categoria: 'General',
    activo: true,
    rolesPermitidos: [],
    orden: 0
  });

  const iconOptions = [
    { value: 'BarChart3', label: 'Gráfico de Barras', icon: BarChart3 },
    { value: 'TrendingUp', label: 'Tendencias', icon: TrendingUp },
    { value: 'Users', label: 'Usuarios', icon: Users },
    { value: 'FileText', label: 'Documentos', icon: FileText }
  ];

  const categoriaOptions = [
    'General',
    'Ejecutivo',
    'Recursos Humanos',
    'Operaciones',
    'Finanzas',
    'Ventas',
    'Marketing'
  ];

  const rolesOptions = [
    'Admin',
    'Head of',
    'Team Leader',
    'Consultor',
    'Viewer'
  ];

  useEffect(() => {
    loadReportes();
  }, []);

  const loadReportes = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getReportes();
      setReportes(data);
    } catch (error) {
      console.error('Error cargando reportes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingReporte) {
        await ApiService.updateReporte(editingReporte.id, formData);
      } else {
        await ApiService.createReporte(formData);
      }
      
      setIsModalOpen(false);
      setEditingReporte(null);
      resetForm();
      loadReportes();
    } catch (error) {
      console.error('Error guardando reporte:', error);
    }
  };

  const handleEdit = (reporte: Reporte) => {
    setEditingReporte(reporte);
    setFormData({
      nombre: reporte.nombre,
      descripcion: reporte.descripcion,
      webUrl: reporte.webUrl,
      embedUrl: reporte.embedUrl,
      datasetId: reporte.datasetId,
      icono: reporte.icono,
      categoria: reporte.categoria,
      activo: reporte.activo,
      rolesPermitidos: reporte.rolesPermitidos || [],
      orden: reporte.orden
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este reporte?')) {
      try {
        await ApiService.deleteReporte(id);
        loadReportes();
      } catch (error) {
        console.error('Error eliminando reporte:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      webUrl: '',
      embedUrl: '',
      datasetId: '',
      icono: 'BarChart3',
      categoria: 'General',
      activo: true,
      rolesPermitidos: [],
      orden: 0
    });
  };

  const handleOpenModal = () => {
    resetForm();
    setEditingReporte(null);
    setIsModalOpen(true);
  };

  const filteredReportes = reportes.filter(reporte =>
    reporte.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reporte.categoria.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reporte.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getIcon = (iconName: string) => {
    const iconOption = iconOptions.find(opt => opt.value === iconName);
    return iconOption ? iconOption.icon : BarChart3;
  };

  const getCategoryColor = (categoria: string) => {
    switch (categoria) {
      case 'Ejecutivo': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700';
      case 'Recursos Humanos': return 'bg-primary/10 dark:bg-primary/20 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'Operaciones': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'Finanzas': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700';
      case 'Ventas': return 'bg-error/10 dark:bg-error/20 text-error border-error/30';
      case 'Marketing': return 'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300 border-pink-200 dark:border-pink-700';
      default: return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver
          </button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">Gestión de Reportes</h1>
            <p className="text-neutral-600 dark:text-neutral-400">Administra los reportes de Power BI disponibles</p>
          </div>
        </div>
        <button
          onClick={handleOpenModal}
          className="btn-primary transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Nuevo Reporte</span>
        </button>
      </div>

      {/* Barra de búsqueda */}
      <div className="card p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar reportes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Lista de reportes */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredReportes.map(reporte => {
            const Icon = getIcon(reporte.icono);
            return (
              <div key={reporte.id} className="card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">{reporte.nombre}</h3>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs border ${getCategoryColor(reporte.categoria)}`}>
                        {reporte.categoria}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    {reporte.activo ? (
                      <Eye className="w-4 h-4 text-success" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />
                    )}
                  </div>
                </div>

                <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-4 line-clamp-2">
                  {reporte.descripcion}
                </p>

                {reporte.rolesPermitidos && reporte.rolesPermitidos.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Roles permitidos:</p>
                    <div className="flex flex-wrap gap-1">
                      {reporte.rolesPermitidos.map(rol => (
                        <span key={rol} className="px-2 py-1 bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 text-xs rounded">
                          {rol}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => window.open(reporte.webUrl, '_blank')}
                      className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:text-neutral-400 p-1"
                      title="Abrir en Power BI"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">Orden: {reporte.orden}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(reporte)}
                      className="text-primary hover:text-blue-800 p-1"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(reporte.id)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal para crear/editar */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-neutral-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50 mb-6">
                {editingReporte ? 'Editar Reporte' : 'Nuevo Reporte'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Nombre del reporte *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Descripción
                    </label>
                    <textarea
                      value={formData.descripcion}
                      onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                      className="input-field"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Categoría
                    </label>
                    <select
                      value={formData.categoria}
                      onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                      className="input-field"
                    >
                      {categoriaOptions.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Ícono
                    </label>
                    <select
                      value={formData.icono}
                      onChange={(e) => setFormData({ ...formData, icono: e.target.value })}
                      className="input-field"
                    >
                      {iconOptions.map(icon => (
                        <option key={icon.value} value={icon.value}>{icon.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      URL Web de Power BI *
                    </label>
                    <input
                      type="url"
                      required
                      value={formData.webUrl}
                      onChange={(e) => setFormData({ ...formData, webUrl: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      URL de Embed de Power BI *
                    </label>
                    <input
                      type="url"
                      required
                      value={formData.embedUrl}
                      onChange={(e) => setFormData({ ...formData, embedUrl: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Dataset ID *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.datasetId}
                      onChange={(e) => setFormData({ ...formData, datasetId: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Orden
                    </label>
                    <input
                      type="number"
                      value={formData.orden}
                      onChange={(e) => setFormData({ ...formData, orden: parseInt(e.target.value) || 0 })}
                      className="input-field"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Roles Permitidos
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {rolesOptions.map(rol => (
                        <label key={rol} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.rolesPermitidos.includes(rol)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  rolesPermitidos: [...formData.rolesPermitidos, rol]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  rolesPermitidos: formData.rolesPermitidos.filter(r => r !== rol)
                                });
                              }
                            }}
                            className="rounded border-neutral-300 dark:border-neutral-700 text-primary focus:ring-primary"
                          />
                          <span className="text-sm text-neutral-700 dark:text-neutral-300">{rol}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.activo}
                        onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-neutral-700 dark:text-neutral-300">Reporte activo</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingReporte(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingReporte ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportesConfig;